/**
 * On-demand instance catalog filters for FlexibleCRUD toolbar.
 */

import React from 'react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import {
  AE_LABEL_COMPACT,
  AE_SELECT_COMPACT,
} from '@/components/assessment-engine/layout/aeTheme';
import { useLanguage } from '@/state/language/LanguageProvider';
import { STUDIO_BINDING_STATUSES } from '@/types/aiStudioAgent';
import type { StudioBindingStatus } from '@/types/aiStudioAgent';

export type InstanceCatalogFilterValues = {
  status: StudioBindingStatus | '';
  setup: '' | 'ready' | 'incomplete';
};

export const EMPTY_INSTANCE_CATALOG_FILTERS: InstanceCatalogFilterValues = {
  status: '',
  setup: '',
};

export function instanceFiltersToQueryParams(
  filters: InstanceCatalogFilterValues
): Record<string, string | undefined> {
  return {
    status: filters.status || undefined,
    setup: filters.setup || undefined,
  };
}

function countActiveFilters(filters: InstanceCatalogFilterValues): number {
  return Object.values(filters).filter((value) => value !== '').length;
}

type ToggleProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: InstanceCatalogFilterValues;
  onChange: (next: InstanceCatalogFilterValues) => void;
};

export const InstanceCatalogFilterToggle: React.FC<ToggleProps> = ({
  open,
  onOpenChange,
  value,
  onChange,
}) => {
  const { t } = useLanguage();
  const activeCount = countActiveFilters(value);
  const active = activeCount > 0;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="instance-catalog-filters-panel"
        onClick={() => onOpenChange(!open)}
        className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] ${
          open || active
            ? 'border-[var(--color-accent-bg)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
            : 'border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)]'
        }`}
      >
        <FunnelIcon className="h-4 w-4" aria-hidden />
        {t('aiStudio.instances.filtersHeading')}
        {active && (
          <span className="rounded-full bg-[var(--color-surface-bg)] px-1.5 text-xs font-semibold text-[var(--color-surface-text)]">
            {activeCount}
          </span>
        )}
      </button>
      {active && (
        <button
          type="button"
          onClick={() => onChange({ ...EMPTY_INSTANCE_CATALOG_FILTERS })}
          className="text-xs font-medium text-[var(--color-accent-bg)] hover:underline focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
        >
          {t('aiStudio.instances.filtersClear')}
        </button>
      )}
    </div>
  );
};

type PanelProps = {
  value: InstanceCatalogFilterValues;
  onChange: (next: InstanceCatalogFilterValues) => void;
};

export const InstanceCatalogFilterPanel: React.FC<PanelProps> = ({ value, onChange }) => {
  const { t } = useLanguage();

  const patch = (partial: Partial<InstanceCatalogFilterValues>) => {
    onChange({ ...value, ...partial });
  };

  return (
    <div
      id="instance-catalog-filters-panel"
      className="border-t border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2 pb-2 pt-2"
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="instance-filter-status">
            {t('aiStudio.instances.filterStatus')}
          </label>
          <select
            id="instance-filter-status"
            className={AE_SELECT_COMPACT}
            value={value.status}
            onChange={(e) => patch({ status: e.target.value as StudioBindingStatus | '' })}
          >
            <option value="">{t('aiStudio.instances.filterAll')}</option>
            {STUDIO_BINDING_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(`aiStudio.bindingStatus.${status}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={AE_LABEL_COMPACT} htmlFor="instance-filter-setup">
            {t('aiStudio.instances.columnSetup')}
          </label>
          <select
            id="instance-filter-setup"
            className={AE_SELECT_COMPACT}
            value={value.setup}
            onChange={(e) =>
              patch({ setup: e.target.value as InstanceCatalogFilterValues['setup'] })
            }
          >
            <option value="">{t('aiStudio.instances.filterAll')}</option>
            <option value="ready">{t('aiStudio.provision.setupReady')}</option>
            <option value="incomplete">{t('aiStudio.provision.setupIncomplete')}</option>
          </select>
        </div>
      </div>
    </div>
  );
};
